import React, { useCallback, useEffect, useState } from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { CSVLink, CSVDownload } from "react-csv";
import moment from "moment";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import { TiExportOutline } from "react-icons/ti";
import { toast } from "react-toastify";
import TableHeader from "components/common/Table/TableHeader";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import { URL_ROSTERING_WORKERS_TIMESHEET_EXPORT_LIST } from "store/services/URL";
import ExportCSVItem from "./ExportCSVItems";
import { getTimesheetLength, getWorkerTimesheetLength } from "Utils/utils";
import { useSelector } from "react-redux";
import { getSpSetting, getSpRosteringSetting } from "store/selector/sp.selector";
import { getParticipantLabel } from "Constants/constant";

const mapExportRowForDisplay = (row) => ({
  participant_name: row.participant_name ?? "",
  worker_name: row.worker_name ?? "",
  shift_date: row.shift_date,
  shift_length: row.shift_length,
  shift_start_time: row.shift_start_time,
  shift_end_time: row.shift_end_time,
  worker_start_time: row.worker_start_time,
  worker_end_time: row.worker_end_time,
  travel_km: row.travel_km,
  service_category: row.service_category,
  ndis_item_name: row.ndis_item_name,
  ndis_item_number: row.ndis_item_number,
});

const escapeCSV = (str) => {
  if (typeof str !== "string") return str;
  let escaped = str.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  escaped = escaped.replace(/"/g, '""');
  if (/[",\n]/.test(escaped)) {
    return `"${escaped}"`;
  }
  return escaped;
};

const ExportCSVModal = (props) => {
  const { isShow, handleClose } = props;

  const spSetting = useSelector((state) => getSpSetting(state));
  const spRosteringSetting = useSelector((state) => getSpRosteringSetting(state));
  const careSector = spRosteringSetting?.care_sector ?? spSetting?.care_sector;
  const participantNameLabel =
    (getParticipantLabel(careSector, false) || "Participant") + " Name";
  const participantLabelPluralWithName =
    (getParticipantLabel(careSector, true) || "Participants") + " name";

  const [isLoading, setIsLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [exportDate, setExportDate] = useState({
    startDate: null,
    endDate: null,
  });
  const [displayShiftsData, setDisplayShiftData] = useState([]);

  const buildExportRows = useCallback(
    (data) => {
      const dataset = [];
      const displayRows = [];

      data?.forEach((timeSheet) => {
        const workerName = `${timeSheet?.first_name ?? ""} ${
          timeSheet?.last_name ?? ""
        }`.trim();
        timeSheet?.timeSheet?.forEach((timesheetItem) => {
          const participantNames = (timesheetItem?.participants ?? [])
            .map(
              (participant) =>
                `${participant?.first_name ?? ""} ${
                  participant?.last_name ?? ""
                }`.trim()
            )
            .filter(Boolean)
            .join(", ");
          const shiftNotes =
            timesheetItem.shift_notes?.length > 0
              ? timesheetItem?.shift_notes
                  ?.filter((note) => note)
                  ?.map(
                    (note) =>
                      `Shift Notes for (${note?.first_name || ""} ${
                        note?.last_name || ""
                      }): ${(note?.notes ? escapeCSV(note?.notes) : "")
                        ?.replace(/\n/g, " ")
                        ?.replace(/,/g, "")}`
                  )
                  ?.join(" | ")
              : "N/A";
          const shiftLength = timesheetItem?.timesheet_length;
          const shiftStartTime = timesheetItem?.start_time;
          const shiftEndTime = timesheetItem?.end_time;
          const shiftDate = timesheetItem?.time_sheet_date;
          const workerStartTime = timesheetItem?.worker_start_time;
          const workerEndTime = timesheetItem?.worker_end_time;
          const shiftKm = timesheetItem?.km;
          const serviceCategory = timesheetItem?.serviceType?.name;
          const itemName = timesheetItem?.invoiceTimesheets
            ? timesheetItem?.invoiceTimesheets[0]?.ndis_support_item_name
            : timesheetItem?.ndisItem?.support_item_name;
          const itemNumber = timesheetItem?.invoiceTimesheets
            ? timesheetItem?.invoiceTimesheets[0]?.ndis_support_item_number
            : timesheetItem?.ndisItem?.support_item_number;
          const workerTotalTime =
            workerStartTime && workerEndTime
              ? getTimesheetLength(workerStartTime, workerEndTime)
              : 0;

          const csvRow = {
            [participantNameLabel]: participantNames,
            "Worker Name": workerName,
            "Shift Notes": shiftNotes ? shiftNotes : "N/A",
            "Shift Date": shiftDate ? shiftDate : "N/A",
            "Shift Length": shiftLength,
            "Shift Start Time": shiftStartTime ? shiftStartTime : "N/A",
            "Shift End Time": shiftEndTime ? shiftEndTime : "N/A",
            "Worker Start Time": workerStartTime ? workerStartTime : "N/A",
            "Worker End Time": workerEndTime ? workerEndTime : "N/A",
            "Worker Total Time": workerTotalTime,
            "Travel KM": shiftKm ? shiftKm : 0,
            "Service Category": serviceCategory,
            "NDIS Item Name": itemName,
            "NDIS Item Number": itemNumber,
          };

          dataset.push(csvRow);
          displayRows.push(
            mapExportRowForDisplay({
              participant_name: participantNames,
              worker_name: workerName,
              shift_date: shiftDate ? shiftDate : "N/A",
              shift_length: shiftLength,
              shift_start_time: shiftStartTime ? shiftStartTime : "N/A",
              shift_end_time: shiftEndTime ? shiftEndTime : "N/A",
              worker_start_time: workerStartTime ? workerStartTime : "N/A",
              worker_end_time: workerEndTime ? workerEndTime : "N/A",
              travel_km: shiftKm ? shiftKm : 0,
              service_category: serviceCategory,
              ndis_item_name: itemName,
              ndis_item_number: itemNumber,
            })
          );
        });
      });

      return { dataset, displayRows };
    },
    [participantNameLabel]
  );

  useEffect(() => {
    if (moment(exportDate.startDate).isAfter(exportDate.endDate)) {
      setIsLoading(false);
      toast.error("Invalid date range. Please select a valid date range.", {
        toastId: "invalidDateToast",
      });
      return;
    }

    if (
      exportDate.startDate &&
      exportDate.endDate &&
      !moment(exportDate.startDate).isAfter(exportDate.endDate)
    ) {
      setIsLoading(true);
      const start_date = exportDate?.startDate;
      const end_date = exportDate?.endDate;

      apiPostRequest(URL_ROSTERING_WORKERS_TIMESHEET_EXPORT_LIST, {
        start_date,
        end_date,
        export: "csv",
      })
        .then((res) => {
          const data = res?.data?.data;
          const { dataset, displayRows } = buildExportRows(data);
          setDisplayShiftData(displayRows);
          setCsvData(dataset);
        })
        .catch((error) => console.log({ error })).finally(() => setIsLoading(false));
    }
  }, [exportDate.startDate, exportDate.endDate, buildExportRows]);

  const handleExportCSV = () => {
    if (moment(exportDate.startDate).isAfter(exportDate.endDate)) {
      setIsLoading(false);
      toast.error("Invalid date range. Please select a valid date range.", {
        toastId: "invalidDateToast",
      });
      return;
    }

    // if (csvData?.length === 0) {
    //   setIsLoading(false);
    //   toast.warning("No timesheet data to export", {
    //     toastId: "noTimesheetToast",
    //   });
    //   return;
    // }

    if (exportDate.startDate && exportDate.endDate) {
        if (
      exportDate.startDate &&
      exportDate.endDate &&
      !moment(exportDate.startDate).isAfter(exportDate.endDate)
    ) {
      setIsLoading(true);
      const start_date = exportDate?.startDate;
      const end_date = exportDate?.endDate;

      apiPostRequest(URL_ROSTERING_WORKERS_TIMESHEET_EXPORT_LIST, {
        start_date,
        end_date,
        download_csv:"download_csv",
      })
        .then((res) => {
          const url = res?.data?.data;

          window.open(url, "_blank");
  
          setIsLoading(false);
        })
        .catch((error) => console.log({ error }));
    }
      // const csvLink = document.getElementById("export_csv");
      // csvLink.click();
      // setExportDate({
      //   startDate: null,
      //   endDate: null,
      // });
      // handleClose();
      // setIsLoading(false);
    }
  };

 

  const handleCancelButton = (date) => {
    if (date === "startDate") {
      setExportDate((prev) => ({
        ...prev,
        startDate: null,
      }));
    } else {
      setExportDate((prev) => ({
        ...prev,
        endDate: null,
      }));
    }
    setDisplayShiftData([]);
  };
  const modalFooter = () => (
    <>
      <CustomButton
        variant='danger'
        clickHandler={() => {
          handleClose();
        }}
        label='Cancel'
      />
      <CustomButton
        clickHandler={handleExportCSV}
        showLoading={isLoading}
        isDisabled={!exportDate.startDate || !exportDate.endDate}
        label={"Export"}
        id='export_csv'
        Icon={<TiExportOutline className='h-5 w-5' />}
      />
    </>
  );

  const ExportContent = () => (
    <>
      <div className='min-w-full  p-4 py-2 flex gap-x-8 '>
        <div className='w-[40%] '>
          <label
            // htmlFor='from-date-to-to-date'
            className='block text-sm font-medium text-gray-700'
          >
            From (Max 1 month range)
          </label>

          <DateRangePickerNew
            isClearable={true}
            isSingleDate
            handleChange={(obj) => {
              setExportDate((prev) => ({
                ...prev,
                endDate: null,
                startDate: obj,
              }));
            }}
            singleSelectedDate={
              exportDate.startDate && new Date(exportDate.startDate)
            }
            handleCancel={() => handleCancelButton("startDate")}
          />
        </div>
        <div className='w-[40%]'>
          <label
            // htmlFor='from-date-to-to-date'
            className='block text-sm font-medium text-gray-700'
          >
            To
          </label>

          <DateRangePickerNew
            isClearable={true}
            isSingleDate
            handleChange={(obj) => {
              setExportDate((prev) => ({
                ...prev,
                endDate: obj,
              }));
            }}
            maxDate={  exportDate.startDate && moment(exportDate.startDate).add(1, 'months').toDate() }
            singleSelectedDate={
              exportDate.endDate && new Date(exportDate.endDate)
            }
            handleCancel={() => handleCancelButton("endDate")}
          />
        </div>

        <CSVLink
          data={csvData}
          id='export_csv'
          className='hidden btn'
          filename={"Export TimeSheet"}
        ></CSVLink>
      </div>
      <div className='p-4 mt-3'>
        {displayShiftsData && displayShiftsData?.length > 0 && (
          <div className='py-2'>
            <h4>
              <b>Total Timesheets:</b> {displayShiftsData?.length}
            </h4>
          </div>
        )}
        <div className=' shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
          <table className='min-w-full divide-y divide-gray-300 border '>
            <thead className='capitalize '>
              <tr>
                <TableHeader
                  isSort={false}
                  label={participantLabelPluralWithName}
                  cssClass='normal-case'
                />
                <TableHeader isSort={false} label={"Worker name"} />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Shift date"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Shift length"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Shift start time"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Shift end time"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Worker Start Time"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Worker End Time"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Worker Total Time"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Travel KM"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Service Category"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Item Name"}
                />
                <TableHeader
                  cssClass='text-center'
                  isSort={false}
                  label={"Item Number"}
                />
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200 bg-white '>
              {isLoading ? (
                <tr>
                  <td colSpan={13} className='bg-gray-50 py-2'>
                    <CircularLoader
                      classes='flex justify-center items-center'
                      classloader='loader-l'
                    />
                  </td>
                </tr>
              ) : displayShiftsData && displayShiftsData?.length > 0 ? (
                displayShiftsData?.map((shift, index) => (
                  <ExportCSVItem shift={shift} index={index} />
                ))
              ) : (
                displayShiftsData?.length === 0 && (
                  <tr>
                    <td colSpan={10} className='py-6 text-center text-red-600'>
                      No Data Found !
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  return (
    <div>
      {isShow && (
        <ModalSkeleton
          title='Export Timesheet'
          isOpen={isShow}
          cssClass='w-[60%] '
          bodyPadding='px-0'
          closeModal={() => {
            handleClose();
          }}
          modalFooter={modalFooter()}
        >
          {false ? (
            <div
              className={`flex flex-col w-full min-h-screen bg-gray-50 items-center justify-center`}
            >
              <CircularLoader
                classes='flex justify-center items-center'
                classloader='loader-l'
              />
            </div>
          ) : (
            <div className='max-h-[600px] custom-scroll'>
              {" "}
              {ExportContent()}
            </div>
          )}
        </ModalSkeleton>
      )}
    </div>
  );
};

export default ExportCSVModal;
